import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Spinner from '@renderer/components/Spinner'
import Button from '@renderer/components/Button'
import { createQrLoadReporter, type QrLoadEventHandler } from '@renderer/utils/qrLoadReporter'

// The payment QR is a Midtrans-hosted image fetched over the network (proxied
// through the backend), not a string drawn locally. A flaky network or a CDN
// hiccup at display time can make the fetch fail, which used to leave a blank /
// broken <img> showing its alt text, with no way for the operator to recover.
// We auto-retry a few times before surfacing a manual reload.
const QR_MAX_AUTO_RETRIES = 4
const QR_RETRY_DELAY_MS = 2000

interface QrCodeImageProps {
  /** Proxy QR URL, e.g. `/api/payments/qr?url=...`. */
  qrUrl: string
  /** Classes applied to the <img> (sizing / rounding differ per template). */
  className?: string
  /**
   * Lifecycle events (loaded, failed attempt, gave up, slow, manual reload, left
   * before loading) so the caller can log them; see `qrActivityLogger`.
   */
  onEvent?: QrLoadEventHandler
}

/**
 * Robust payment-QR renderer shared across booth templates. While the image
 * loads it shows the existing Spinner instead of a half-rendered <img>, never
 * displays alt text (alt=""), auto-retries the fetch (cache-busted) on error,
 * and finally falls back to a manual "Reload" button so a transient network /
 * CDN failure never strands the customer on a blank QR.
 */
const QrCodeImage = ({ qrUrl, className = '', onEvent }: QrCodeImageProps): React.JSX.Element => {
  const { t } = useTranslation()
  const [loaded, setLoaded] = useState(false)
  const [failed, setFailed] = useState(false)
  // Monotonic cache-bust counter: bumping it changes the <img> src + key so the
  // browser actually re-fetches instead of reusing the failed response.
  const [bust, setBust] = useState(0)
  // Authoritative auto-retry attempt count, kept in a ref so the error handler
  // always reads a fresh value without needing to re-create itself.
  const attemptRef = useRef(0)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Callers pass inline handlers; read through a ref so a re-render never
  // restarts the load cycle (and its timings).
  const onEventRef = useRef(onEvent)
  onEventRef.current = onEvent
  const reporterRef = useRef<ReturnType<typeof createQrLoadReporter> | null>(null)
  if (!reporterRef.current) {
    reporterRef.current = createQrLoadReporter((event) => onEventRef.current?.(event))
  }
  const reporter = reporterRef.current
  const unmountingRef = useRef(false)

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current)
      retryTimerRef.current = null
    }
  }, [])

  // Declared before the cycle effect so its cleanup runs first on unmount and
  // the cycle can tell an unmount from a URL change.
  useLayoutEffect(() => {
    unmountingRef.current = false
    return (): void => {
      unmountingRef.current = true
    }
  }, [])

  // Layout effect so the cycle starts before the <img> can fire load/error.
  useLayoutEffect(() => {
    reporter.begin(qrUrl)
    return (): void => reporter.end(unmountingRef.current ? 'unmount' : 'url-change')
  }, [qrUrl, reporter])

  // A fresh QR URL (new transaction) resets all load tracking.
  useEffect(() => {
    clearRetryTimer()
    attemptRef.current = 0
    setLoaded(false)
    setFailed(false)
    setBust(0)
    return clearRetryTimer
  }, [qrUrl, clearRetryTimer])

  const handleLoad = useCallback(() => {
    clearRetryTimer()
    reporter.loaded()
    setLoaded(true)
    setFailed(false)
  }, [clearRetryTimer, reporter])

  const handleError = useCallback(() => {
    clearRetryTimer()
    setLoaded(false)
    const willRetry = attemptRef.current < QR_MAX_AUTO_RETRIES
    reporter.failed(willRetry)
    if (!willRetry) {
      setFailed(true)
      return
    }
    retryTimerRef.current = setTimeout(() => {
      attemptRef.current += 1
      reporter.retrying()
      setBust((prev) => prev + 1)
    }, QR_RETRY_DELAY_MS)
  }, [clearRetryTimer, reporter])

  const handleManualReload = useCallback(() => {
    clearRetryTimer()
    reporter.manualReload()
    attemptRef.current = 0
    setLoaded(false)
    setFailed(false)
    setBust((prev) => prev + 1)
  }, [clearRetryTimer, reporter])

  if (failed) {
    return (
      <div className="flex h-full w-full max-w-sm flex-col items-center justify-center gap-4 text-center">
        <p className="text-sm text-gray-600">
          {t('payment.qrLoadFailed', "Couldn't load the QR code.")}
        </p>
        <Button text={t('payment.qrReload', 'Reload QR code')} onClick={handleManualReload} />
      </div>
    )
  }

  // Cache-bust only after the first failure so the happy path keeps the
  // original (cacheable) URL. The proxy ignores the extra `_r` param.
  const src = bust > 0 ? `${qrUrl}${qrUrl.includes('?') ? '&' : '?'}_r=${bust}` : qrUrl

  return (
    <div className="relative h-full w-full">
      <img
        key={bust}
        src={src}
        alt=""
        className={className}
        crossOrigin="anonymous"
        onLoad={handleLoad}
        onError={handleError}
        style={loaded ? undefined : { visibility: 'hidden' }}
      />
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Spinner />
        </div>
      )}
    </div>
  )
}

export default QrCodeImage
